import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { YearMonthCalendar } from "@/components/ui/year-month-calendar";
import { getAppIcon, getAppColor, formatCurrency, getRenewalUrgencyColor, calculateDaysUntil } from "@/lib/utils";
import { License } from "@shared/schema";
import { Calendar as CalendarIcon, CheckCircle, X } from "lucide-react";

export default function RenewalCalendar() {
  const [, navigate] = useLocation();
  const [selectedMonth, setSelectedMonth] = useState<Date>(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1); // First day of current month
  });
  
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedRenewals, setSelectedRenewals] = useState<any[]>([]);
  
  // Define the months array for dropdowns
  const months = [
    "January", "February", "March", "April", "May", "June", 
    "July", "August", "September", "October", "November", "December"
  ];
  
  // Generate years array for dropdowns (current year +/- 5 years)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);
  
  const { data: renewals, isLoading } = useQuery({
    queryKey: ['/api/renewals'],
    select: (data) => data || []
  });

  // Get all renewal dates with associated license info
  const getRenewalDates = () => {
    if (!renewals || !Array.isArray(renewals)) return [];
    
    return renewals.filter((license: any) => {
      const status = String(license.status || "").toLowerCase();
      const billingCycle = String(license.billingCycle || "").toLowerCase();
      return status !== "discontinued" && billingCycle !== "one-time" && billingCycle !== "lifetime";
    }).map((license: any) => {
      const renewalDate = new Date(license.renewalDate);
      return {
        date: renewalDate,
        license,
        application: license.application
      };
    });
  };
  
  // Get renewals for the selected month
  const getMonthRenewals = () => {
    if (!selectedMonth || !renewals) return [];
    
    return getRenewalDates().filter((item: {date: Date, license: any, application: any}) => {
      return item.date.getMonth() === selectedMonth.getMonth() &&
        item.date.getFullYear() === selectedMonth.getFullYear();
    }).sort((a: {date: Date}, b: {date: Date}) => a.date.getTime() - b.date.getTime());
  };
  
  // Get renewals for a specific date
  const getRenewalsForDate = (date: Date) => {
    if (!date || !renewals) return [];
    
    return getRenewalDates().filter((item: {date: Date}) => {
      return item.date.getDate() === date.getDate() &&
        item.date.getMonth() === date.getMonth() && 
        item.date.getFullYear() === date.getFullYear();
    }).sort((a: {date: Date}, b: {date: Date}) => a.date.getTime() - b.date.getTime());
  };
  
  // Handle day click in the calendar
  const handleDayClick = (day: Date | undefined) => {
    setSelectedDate(day);
    if (day) {
      setSelectedRenewals(getRenewalsForDate(day));
    } else {
      setSelectedRenewals([]);
    }
  };
  
  // Helper function to determine if two dates represent the same day
  const isSameDay = (date1: Date, date2: Date) => {
    return (
      date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear()
    );
  };
  
  // Create the modifiers for the calendar's renewal date highlighting
  const modifiers = {
    urgentRenewal: (date: Date) => {
      if (!renewals) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      return getRenewalDates().some(item => {
        if (!item.date) return false;
        
        // Check if the calendar date matches a renewal date
        if (!isSameDay(date, item.date)) return false;
        
        // Calculate days until renewal from today
        const itemDate = new Date(item.date);
        itemDate.setHours(0, 0, 0, 0);
        
        const daysUntil = calculateDaysUntil(itemDate);
        
        // Return true if it's urgent (≤ 7 days)
        return daysUntil >= 0 && daysUntil <= 7;
      });
    },
    
    soonRenewal: (date: Date) => {
      if (!renewals) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      return getRenewalDates().some(item => {
        if (!item.date) return false;
        
        // Check if the calendar date matches a renewal date
        if (!isSameDay(date, item.date)) return false;
        
        // Calculate days until renewal from today
        const itemDate = new Date(item.date);
        itemDate.setHours(0, 0, 0, 0);
        
        const daysUntil = calculateDaysUntil(itemDate);
        
        // Return true if it's soon (8-30 days)
        return daysUntil > 7 && daysUntil <= 30;
      });
    },
    
    futureRenewal: (date: Date) => {
      if (!renewals) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      return getRenewalDates().some(item => {
        if (!item.date) return false;
        
        // Check if the calendar date matches a renewal date
        if (!isSameDay(date, item.date)) return false;
        
        // Calculate days until renewal from today
        const itemDate = new Date(item.date);
        itemDate.setHours(0, 0, 0, 0);
        
        const diffTime = itemDate.getTime() - today.getTime();
        const daysUntil = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        // Return true if it's in the future (> 30 days)
        return daysUntil > 30;
      });
    }
  };
  
  const monthRenewals = selectedDate ? selectedRenewals : getMonthRenewals();
  
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <Card className="lg:col-span-1">
        <CardContent className="p-6">
          <div className="flex flex-col space-y-6">
            <label className="text-sm font-medium">Month</label>
            
            <div className="flex gap-4">
              <Select 
                value={selectedMonth.getMonth().toString()} 
                onValueChange={(monthVal) => {
                  const newMonth = new Date(selectedMonth);
                  newMonth.setMonth(parseInt(monthVal));
                  setSelectedMonth(newMonth);
                  setSelectedDate(undefined);
                  setSelectedRenewals([]);
                }}
              >
                <SelectTrigger>
                  <SelectValue>
                    {selectedMonth ? selectedMonth.toLocaleDateString('en-US', { month: 'long' }) : 'Month'}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {months.map((month: string, idx: number) => (
                    <SelectItem key={month} value={idx.toString()}>
                      {month}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select 
                value={selectedMonth.getFullYear().toString()} 
                onValueChange={(yearVal) => {
                  const newMonth = new Date(selectedMonth);
                  newMonth.setFullYear(parseInt(yearVal));
                  setSelectedMonth(newMonth);
                  setSelectedDate(undefined);
                  setSelectedRenewals([]);
                }}
              >
                <SelectTrigger>
                  <SelectValue>
                    {selectedMonth ? selectedMonth.getFullYear().toString() : 'Year'}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {years.map((year: number) => (
                    <SelectItem key={year} value={year.toString()}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-6 relative">
            {/* Custom styling for date indicators */}
            <div className="renewal-calendar-container">
              <YearMonthCalendar
                mode="single"
                selected={selectedDate}
                onSelect={handleDayClick}
                className="rounded-md border"
                showRenewalStatus={true}
                month={selectedMonth}
                onMonthChange={(date) => setSelectedMonth(date)}
                fromMonth={new Date(new Date().getFullYear(), 0)} // January of current year
                toMonth={new Date(new Date().getFullYear() + 1, 11)} // December of next year
                hideSelectors={true}
              />
            </div>
          </div>

          <div className="mt-6 space-y-2">
            <p className="text-sm font-medium">License Renewal Status</p>
            <div className="flex flex-wrap gap-2">
              <Badge className="bg-red-100 text-red-800 hover:bg-red-100 border-2 border-red-500 font-medium">
                <span className="mr-1 text-red-600 font-bold">!</span> 
                Urgent (within 7 days)
              </Badge>
              <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 border-2 border-amber-500 font-medium">
                <span className="mr-1 h-2 w-2 rounded-full bg-amber-500 inline-block"></span>
                Soon (this month)
              </Badge>
              <Badge className="bg-green-100 text-green-800 hover:bg-green-100 border-2 border-green-500 font-medium">
                <span className="mr-1 h-2 w-2 rounded-full bg-green-500 inline-block"></span>
                Future renewal
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="lg:col-span-2">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold mb-4">
            {selectedDate ? (
              <>
                Renewals for {selectedDate.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })}
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="ml-2 text-xs"
                  onClick={() => {
                    setSelectedDate(undefined);
                    setSelectedRenewals([]);
                  }}
                >
                  <X className="h-3.5 w-3.5 mr-1" />
                  Clear
                </Button>
              </>
            ) : (
              <>{selectedMonth?.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })} Renewals</>
            )}
          </h3>

          {isLoading ? (
            <div className="space-y-4">
              {[...Array(3)].map((_, index) => (
                <Skeleton key={index} className="h-24 w-full" />
              ))}
            </div>
          ) : monthRenewals.length === 0 ? (
            <div className="text-center py-8 bg-slate-50 rounded-lg border border-dashed border-slate-200">
              <CheckCircle className="h-10 w-10 text-slate-400 mx-auto mb-2" />
              <p className="text-slate-500">No license renewals for this month.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {monthRenewals.map((item, index) => {
                const { license, application, date } = item;
                const now = new Date();
                const daysToRenewal = Math.floor((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                const urgencyColor = getRenewalUrgencyColor(daysToRenewal);
                
                return (
                  <div 
                    key={index} 
                    className={`border-2 rounded-lg p-4 hover:bg-slate-50 ${
                      daysToRenewal <= 0 
                        ? 'border-red-500 bg-red-50' 
                        : daysToRenewal <= 7 
                          ? 'border-red-400 bg-red-50/50' 
                          : daysToRenewal <= 30 
                            ? 'border-amber-400 bg-amber-50/50' 
                            : 'border-green-400 bg-green-50/50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className={`flex-shrink-0 h-12 w-12 rounded ${getAppColor(application?.logo)} flex items-center justify-center`}>
                          <i className={`${getAppIcon(application?.logo)} text-xl`}></i>
                        </div>
                        <div className="ml-4">
                          <div className="flex items-center">
                            <h4 className="text-base font-medium text-slate-800">{application?.name}</h4>
                            <Badge className={`ml-2 ${urgencyColor} font-semibold`}>
                              {daysToRenewal <= 0 
                                ? 'OVERDUE' 
                                : daysToRenewal <= 7 
                                  ? `${daysToRenewal} DAY${daysToRenewal === 1 ? '' : 'S'} LEFT` 
                                  : date.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-500 mt-0.5">
                            {license.name} • {license.type || 'Standard'} • {license.totalSeats} Seats
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-semibold">{formatCurrency(license.cost)}</div>
                        <div className="text-xs text-slate-500">{license.billingCycle}</div>
                      </div>
                    </div>
                    <div className="mt-3 flex justify-end">
                      <Button 
                        variant={daysToRenewal <= 7 ? "default" : "outline"}
                        size="sm"
                        onClick={() => navigate(`/licenses/${license.id}`)}
                        className={`text-xs px-3 py-1 rounded ${
                          daysToRenewal <= 7 
                            ? "bg-red-600 hover:bg-red-700" 
                            : "border border-primary text-primary hover:bg-primary/5"
                        }`}
                      >
                        {daysToRenewal <= 7 ? "Manage Now" : "Manage"}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
